import { useState } from "react";
import DataTable from "@/components/controls/DataTable/DataTable";
import ExpandedDreamerTableModal from "@/components/global/Chat/components/dreamerCharts/table/ExpandedDreamerTableModal";
import DreamerAttachmentMessageCard from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentMessageCard";
import DreamerAttachmentEmptyState, {
  DreamerAttachmentEmptyVariant
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentEmptyState";
import DreamerAttachmentPreviewFooter from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentPreviewFooter";
import ScheduleModal from "@/components/global/Chat/components/ScheduleModal/ScheduleModal";
import RecurringReportsModal from "@/components/global/Chat/components/dreamerReports/RecurringReportsModal";
import {
  ChartTableViewMode,
  useChartTableViewTabOptions
} from "@/components/global/Chat/components/dreamerCharts/table/useChartTableViewTabOptions";
import { useDreamerAttachmentData } from "@/components/global/Chat/components/dreamerAttachment/useDreamerAttachmentData";
import { useScheduleConfirmation } from "@/components/global/Chat/components/dreamerReports/useScheduleConfirmation";
import { useChatReportSave } from "@/components/global/Chat/components/dreamerReports/useChatReportSave";
import { useReportModalHeader } from "@/components/global/Chat/components/dreamerReports/useReportModalHeader";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import {
  resolveIsSchedulable,
  useControllableExpansion
} from "@/components/global/Chat/components/dreamerCharts/useControllableExpansion";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";
import { useDeleteReportingScheduler } from "@/entities/reportingScheduler/hooks";
import { useMessageReportRow } from "@/components/global/Chat/components/dreamerCharts/useMessageReportRow";
import { useChat } from "@/components/layouts/Chat/ChatContext";

const CHART_DISABLED_TOOLTIP_KEY = "chat.tooltips.chartUnavailable";

export const MAX_ROWS_IN_PREVIEW = 25;
const FALLBACK_TITLE = "Data table";

interface TableMessageProps {
  metadata: DreamerAttachmentFilterMetadata;
  messageId: string;
  isSchedulable: boolean;
  isReplayable?: boolean;
  savedReportId?: string | null;
  scheduleId: string | null;
  /**
   * What the user currently calls the saved report this message belongs to.
   * The card shows it in place of the generated title, and the download saves
   * under it — a rename writes the report row, which the chart's own title,
   * minted by the brain with the data, can never follow.
   */
  savedReportName?: string | null;
  defaultExpanded?: boolean;
  /** Controls the expanded modal, for callers that outlive one opening. */
  isExpanded?: boolean;
  onExpandedClose?: () => void;
}

const TableMessage = ({
  metadata,
  messageId,
  isSchedulable,
  isReplayable = false,
  savedReportId = null,
  scheduleId,
  savedReportName = null,
  defaultExpanded = false,
  isExpanded,
  onExpandedClose
}: TableMessageProps): JSX.Element => {
  const appendConfirmation = useScheduleConfirmation();

  const [localScheduleId, setLocalScheduleId] = useState<
    string | null | undefined
  >(undefined);
  const effectiveScheduleId =
    localScheduleId !== undefined ? localScheduleId : scheduleId;

  const {
    row: reportRow,
    schedule: scheduleData,
    isRowLoading,
    isRowFailed
  } = useMessageReportRow(effectiveScheduleId, savedReportId);

  const effectiveIsSchedulable = resolveIsSchedulable(
    isSchedulable,
    isReplayable,
    scheduleId
  );

  // The report's own name wins where there is one: it is the only one of
  // the two the user can change.
  const reportName = savedReportName || metadata.title || FALLBACK_TITLE;

  const deleteScheduler = useDeleteReportingScheduler();
  const { forgetSavedReport } = useChat();
  const { forgetReportRun } = useReportRuns();
  const handleCancelSchedule = () => {
    const deletedId = scheduleData?.id;
    if (!deletedId) return;
    deleteScheduler.mutate(
      { pathParams: { id: deletedId } },
      {
        onSuccess: () => {
          setLocalScheduleId(null);
          // The row is gone, so no answer may keep pointing a save at it.
          forgetSavedReport(deletedId);
          forgetReportRun(deletedId);
          appendConfirmation({ kind: "canceled", name: reportName });
        }
      }
    );
  };

  const { isExpandedOpen, expand, setIsExpandedOpen } =
    useControllableExpansion({ isExpanded, defaultExpanded, onExpandedClose });
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const reportSave = useChatReportSave({
    messageId,
    reportName,
    savedReportId,
    isScheduled: !!scheduleData,
    scheduleConfig: scheduleData?.scheduleConfig ?? null,
    isSchedulable: effectiveIsSchedulable,
    onSchedule: () => setIsScheduleModalOpen(true),
    onScheduleCleared: () => setLocalScheduleId(null)
  });
  const modalHeader = useReportModalHeader({
    savedReportId: reportSave.savedReportId,
    savedName: reportRow?.name,
    messageId,
    isReplayable,
    // Only a view another surface controls is one that follows the report's runs.
    regeneratesInPlace: isExpanded !== undefined,
    renderSaveControl: reportSave.renderSaveControl,
    onClose: () => setIsExpandedOpen(false)
  });
  const [viewMode, setViewMode] = useState<ChartTableViewMode>("table");
  const {
    attachmentId,
    rows: tableRows,
    totalRows,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    download,
    isDownloading,
    sorting,
    setSorting,
    filters,
    setFilters,
    hasActiveFilters,
    hasSearchTerm,
    previewColumns,
    sortableColumns,
    filterOptions
  } = useDreamerAttachmentData(metadata, {
    downloadFilenameStem: savedReportName
  });

  const emptyVariant: DreamerAttachmentEmptyVariant | null = error
    ? "error"
    : isLoading
      ? null
      : tableRows.length > 0
        ? null
        : hasActiveFilters
          ? "filters"
          : hasSearchTerm
            ? "search"
            : "noData";

  const viewTabOptions = useChartTableViewTabOptions({
    disabledMode: "chart",
    disabledTooltipKey: CHART_DISABLED_TOOLTIP_KEY
  });

  const tableTitle = reportName;
  const previewFooter =
    !emptyVariant && totalRows > MAX_ROWS_IN_PREVIEW ? (
      <DreamerAttachmentPreviewFooter
        shown={MAX_ROWS_IN_PREVIEW}
        total={totalRows}
        unit="rows"
        onViewAll={expand}
      />
    ) : undefined;

  return (
    <DreamerAttachmentMessageCard
      title={tableTitle}
      viewTabOptions={viewTabOptions}
      viewMode={viewMode}
      onViewModeChange={(v) => setViewMode(v as ChartTableViewMode)}
      disabledOptions={["chart"]}
      tabsDataTest="table-message-view-tabs"
      onExpand={expand}
      expandAriaLabel="Expand table"
      onDownload={download}
      isDownloading={isDownloading}
      canDownload={!!attachmentId}
      downloadAriaLabel="Download attachment"
      onSchedule={() => setIsScheduleModalOpen(true)}
      onCancelSchedule={handleCancelSchedule}
      onManageSchedules={() => setIsManageModalOpen(true)}
      scheduleData={scheduleData}
      isSchedulable={effectiveIsSchedulable}
      saveControl={reportSave.saveControl}
      footer={previewFooter}
      modal={
        <>
          <ExpandedDreamerTableModal
            isOpen={isExpandedOpen}
            setIsOpen={setIsExpandedOpen}
            title={modalHeader.title ?? tableTitle}
            headerSlots={modalHeader.headerSlots}
            columns={sortableColumns}
            data={tableRows}
            isLoading={isLoading}
            generatedAt={metadata.generatedAt}
            hasMore={hasNextPage && !error}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            sorting={sorting}
            onSortingChange={setSorting}
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            emptyVariant={emptyVariant}
            onDownload={download}
            isDownloading={isDownloading}
            canDownload={!!attachmentId}
          />
          <ScheduleModal
            isOpen={isScheduleModalOpen}
            setIsOpen={setIsScheduleModalOpen}
            messageId={messageId}
            reportName={reportName}
            savedReportId={reportSave.savedReportId}
            existingScheduleData={reportRow}
            isReportRowLoading={isRowLoading}
            isReportRowFailed={isRowFailed}
            onCreated={(created) => {
              setLocalScheduleId(created.id);
              reportSave.markScheduled(created.id);
            }}
            onCanceled={() => {
              reportSave.markUnscheduled();
              setLocalScheduleId(null);
              appendConfirmation({ kind: "canceled", name: reportName });
            }}
          />
          <RecurringReportsModal
            isOpen={isManageModalOpen}
            setIsOpen={setIsManageModalOpen}
          />
        </>
      }
    >
      <DataTable
        columns={previewColumns}
        data={tableRows.slice(0, MAX_ROWS_IN_PREVIEW)}
        isDataLoading={isLoading}
        headerClassName="border-b border-border-border1 bg-surface-elevation2"
        className="w-full max-h-[300px]"
        customEmptyState={
          emptyVariant ? (
            <DreamerAttachmentEmptyState variant={emptyVariant} />
          ) : undefined
        }
      />
    </DreamerAttachmentMessageCard>
  );
};

export default TableMessage;
